import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ChevronDown, ChevronRight, Loader2, ExternalLink, 
  LayoutGrid, Users, Calendar, BarChart, ChevronsRight, Maximize2, 
  Minimize2, Filter, Download, Map, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip } from "@/components/ui/tooltip";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { OrganizationHierarchyNode, fetchCompleteHierarchyForOrganization, 
  fetchHierarchyMetrics, fetchHierarchyPathToOrganization } from "@/services/organization/hierarchyService";
import { useToast } from "@/hooks/use-toast";

interface EnhancedOrganizationHierarchyProps {
  organizationId: string;
  className?: string;
  initialView?: "tree" | "grid" | "path" | "metrics";
  defaultDepth?: number;
  showControls?: boolean;
  showSearch?: boolean;
  showMetrics?: boolean;
  size?: "sm" | "md" | "lg";
  initiallyExpanded?: boolean;
}

export function EnhancedOrganizationHierarchy({
  organizationId,
  className = "",
  initialView = "tree",
  defaultDepth = 3,
  showControls = true,
  showSearch = true,
  showMetrics = true,
  size = "md",
  initiallyExpanded = false,
}: EnhancedOrganizationHierarchyProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [view, setView] = useState<"tree" | "grid" | "path" | "metrics">(initialView);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maxDepth, setMaxDepth] = useState(defaultDepth);
  const [hierarchyData, setHierarchyData] = useState<{
    rootNode: OrganizationHierarchyNode | null;
    ancestors: OrganizationHierarchyNode[];
    siblings: OrganizationHierarchyNode[];
    descendants: OrganizationHierarchyNode[];
    flattenedHierarchy: OrganizationHierarchyNode[];
  }>({
    rootNode: null,
    ancestors: [],
    siblings: [],
    descendants: [],
    flattenedHierarchy: [],
  });
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  const [hierarchyMetrics, setHierarchyMetrics] = useState<{
    organizationCount: number;
    metrics: {
      total_contacts: number;
      total_opportunities: number;
      total_opportunity_value: number;
      total_activities: number;
      max_depth: number;
    };
  } | null>(null);
  const [pathToOrg, setPathToOrg] = useState<OrganizationHierarchyNode[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterTier, setFilterTier] = useState<string>("all");

  useEffect(() => {
    if (initiallyExpanded) {
      // Start with root node expanded
      setExpandedNodes({[organizationId]: true});
    }
    loadHierarchyData();
  }, [organizationId, maxDepth]);

  const loadHierarchyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load hierarchy data
      const data = await fetchCompleteHierarchyForOrganization(organizationId, {
        maxDepth,
        includeMetrics: showMetrics,
        includeRoot: true,
        includeSiblings: true,
      });

      setHierarchyData(data);

      if (showMetrics) {
        // Load hierarchy metrics
        const metrics = await fetchHierarchyMetrics(organizationId);
        setHierarchyMetrics(metrics);
      }

      // Load path to organization
      const path = await fetchHierarchyPathToOrganization(organizationId);
      setPathToOrg(path);

    } catch (err) {
      setError("Failed to load organization hierarchy");
      toast({
        title: "Error",
        description: "Failed to load organization hierarchy",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const expandAll = () => {
    const allNodes: Record<string, boolean> = {};
    hierarchyData.flattenedHierarchy.forEach(node => {
      allNodes[node.id] = true;
    });
    setExpandedNodes(allNodes);
  };

  const collapseAll = () => {
    setExpandedNodes({[organizationId]: true});
  };

  const exportHierarchy = () => {
    if (!hierarchyData.rootNode) return;

    const flatData = hierarchyData.flattenedHierarchy.map(org => ({
      id: org.id,
      name: org.name,
      status: org.status,
      tier: org.tier,
      parent_id: org.parent_id,
      depth: org.depth,
      contacts: org.metrics?.contactCount || 0,
      opportunities: org.metrics?.opportunityCount || 0,
      opportunity_value: org.metrics?.opportunityValue || 0,
      activities: org.metrics?.activityCount || 0,
    }));

    const csvContent = [
      // Header row
      Object.keys(flatData[0]).join(","),
      // Data rows
      ...flatData.map(row => Object.values(row).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${hierarchyData.rootNode.name}_hierarchy.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Filter organizations based on search and filters
  const filteredOrganizations = useMemo(() => {
    if (!hierarchyData.flattenedHierarchy) return [];

    return hierarchyData.flattenedHierarchy.filter(org => {
      // Apply search filter
      const matchesSearch = searchQuery.trim() === "" || 
        org.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Apply status filter
      const matchesStatus = filterStatus === "all" || 
        org.status === filterStatus;
      
      // Apply tier filter
      const matchesTier = filterTier === "all" || 
        org.tier === filterTier;
      
      return matchesSearch && matchesStatus && matchesTier;
    });
  }, [hierarchyData.flattenedHierarchy, searchQuery, filterStatus, filterTier]);

  // Calculate total metrics for the filtered organizations
  const filteredMetrics = useMemo(() => {
    return filteredOrganizations.reduce((acc, org) => {
      return {
        contacts: (acc.contacts || 0) + (org.metrics?.contactCount || 0),
        opportunities: (acc.opportunities || 0) + (org.metrics?.opportunityCount || 0),
        opportunityValue: (acc.opportunityValue || 0) + (org.metrics?.opportunityValue || 0),
        activities: (acc.activities || 0) + (org.metrics?.activityCount || 0),
      };
    }, {
      contacts: 0,
      opportunities: 0,
      opportunityValue: 0,
      activities: 0,
    });
  }, [filteredOrganizations]);

  // Get unique status and tier values for filters
  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    hierarchyData.flattenedHierarchy.forEach(org => {
      if (org.status) statuses.add(org.status);
    });
    return Array.from(statuses);
  }, [hierarchyData.flattenedHierarchy]);

  const tierOptions = useMemo(() => {
    const tiers = new Set<string>();
    hierarchyData.flattenedHierarchy.forEach(org => {
      if (org.tier) tiers.add(org.tier);
    });
    return Array.from(tiers);
  }, [hierarchyData.flattenedHierarchy]);

  // Render tree node based on depth, children and expandable state
  const renderTreeNode = (node: OrganizationHierarchyNode, depth: number = 0) => {
    const isExpanded = !!expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const hasManyChildren = node.children && node.children.length > 5;
    
    const containerPadding = {
      sm: 'pl-2',
      md: 'pl-4',
      lg: 'pl-6',
    }[size];

    return (
      <div key={node.id} className="mb-2">
        <div className="flex items-start group">
          <div className="mr-1 pt-0.5">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.id)}
                className="h-5 w-5 rounded-full flex items-center justify-center hover:bg-gray-100"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <ChevronsRight className="h-4 w-4 text-muted-foreground ml-0.5" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center">
              <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
              <Link
                to={`/organizations/${node.id}`}
                className="font-medium hover:underline hover:text-primary"
              >
                {node.name}
              </Link>
              
              <div className="ml-2 flex items-center space-x-1">
                <Badge variant={node.status === "active" ? "outline" : "secondary"}>
                  {node.status}
                </Badge>
                {node.tier && (
                  <Badge variant="outline" className="capitalize">
                    {node.tier}
                  </Badge>
                )}
              </div>
              
              {showMetrics && node.metrics && (
                <div className="ml-auto flex items-center space-x-2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                  {node.metrics.contactCount !== undefined && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            <span>{node.metrics.contactCount}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{node.metrics.contactCount} Contacts</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {node.metrics.opportunityCount !== undefined && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <BarChart className="h-3 w-3 mr-1" />
                            <span>{node.metrics.opportunityCount}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{node.metrics.opportunityCount} Opportunities</p>
                          {node.metrics.opportunityValue !== undefined && (
                            <p>${node.metrics.opportunityValue.toLocaleString()}</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {node.metrics.activityCount !== undefined && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>{node.metrics.activityCount}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{node.metrics.activityCount} Activities</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}
            </div>

            {isExpanded && hasChildren && (
              <div className={`mt-2 ${containerPadding}`}>
                {node.children?.map(childNode => renderTreeNode(childNode, depth + 1))}
                
                {hasManyChildren && !isExpanded && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {node.children?.length} sub-organizations
                  </div>
                )}
              </div>
            )}
            
            {!isExpanded && hasChildren && (
              <div className="text-xs text-muted-foreground mt-1 ml-6">
                {node.children?.length} sub-organizations
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render grid view of organizations
  const renderGridView = () => {
    const gridContainerClass = {
      sm: 'grid-cols-1 gap-2',
      md: 'grid-cols-2 gap-4',
      lg: 'grid-cols-3 gap-6',
    }[size];

    return (
      <div className={`grid ${gridContainerClass}`}>
        {filteredOrganizations.map(org => (
          <Card key={org.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium hover:underline hover:text-primary text-base"
                  >
                    {org.name}
                  </Link>
                </div>
                
                <div className="flex mt-2 space-x-1">
                  <Badge variant={org.status === "active" ? "outline" : "secondary"}>
                    {org.status}
                  </Badge>
                  {org.tier && (
                    <Badge variant="outline" className="capitalize">
                      {org.tier}
                    </Badge>
                  )}
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/organizations/${org.id}`)}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            
            {showMetrics && org.metrics && (
              <div className="grid grid-cols-4 gap-2 mt-4 text-xs">
                <div className="flex flex-col items-center justify-center p-2 bg-muted rounded">
                  <Users className="h-4 w-4 mb-1" />
                  <span className="font-semibold">{org.metrics.contactCount || 0}</span>
                  <span className="text-muted-foreground">Contacts</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-2 bg-muted rounded">
                  <BarChart className="h-4 w-4 mb-1" />
                  <span className="font-semibold">{org.metrics.opportunityCount || 0}</span>
                  <span className="text-muted-foreground">Opps</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-2 bg-muted rounded">
                  <Calendar className="h-4 w-4 mb-1" />
                  <span className="font-semibold">{org.metrics.activityCount || 0}</span>
                  <span className="text-muted-foreground">Activities</span>
                </div>
                
                <div className="flex flex-col items-center justify-center p-2 bg-muted rounded">
                  <Building2 className="h-4 w-4 mb-1" />
                  <span className="font-semibold">{org.metrics.childOrganizationCount || 0}</span>
                  <span className="text-muted-foreground">Sub-orgs</span>
                </div>
              </div>
            )}
            
            {org.parent_id && (
              <div className="mt-3 text-xs text-muted-foreground">
                Parent: {hierarchyData.flattenedHierarchy.find(p => p.id === org.parent_id)?.name}
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  // Render path view (breadcrumb-like)
  const renderPathView = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center flex-wrap">
          {pathToOrg.map((org, index) => (
            <React.Fragment key={org.id}>
              <Link
                to={`/organizations/${org.id}`}
                className={`hover:underline ${org.id === organizationId ? 'font-medium text-primary' : 'text-muted-foreground'}`}
              >
                {org.name}
              </Link>
              {index < pathToOrg.length - 1 && (
                <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {showMetrics && hierarchyData.rootNode && (
          <Card className="p-4">
            <h3 className="font-medium text-base mb-3">Hierarchy Overview</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-muted p-3 rounded text-center">
                <div className="text-2xl font-bold">{hierarchyData.descendants.length + 1}</div>
                <div className="text-sm text-muted-foreground">Organizations</div>
              </div>
              <div className="bg-muted p-3 rounded text-center">
                <div className="text-2xl font-bold">
                  {hierarchyMetrics?.metrics.total_contacts || 0}
                </div>
                <div className="text-sm text-muted-foreground">Contacts</div>
              </div>
              <div className="bg-muted p-3 rounded text-center">
                <div className="text-2xl font-bold">
                  {hierarchyMetrics?.metrics.total_opportunities || 0}
                </div>
                <div className="text-sm text-muted-foreground">Opportunities</div>
              </div>
              <div className="bg-muted p-3 rounded text-center">
                <div className="text-2xl font-bold">
                  ${(hierarchyMetrics?.metrics.total_opportunity_value || 0).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">Pipeline Value</div>
              </div>
            </div>
          </Card>
        )}
        
        <div>
          <h4 className="text-sm font-medium mb-2">Direct Sub-Organizations</h4>
          {hierarchyData.rootNode?.children?.length ? (
            <div className="pl-4 border-l-2 border-muted space-y-2">
              {hierarchyData.rootNode.children.map(child => (
                <div key={child.id} className="flex items-center group">
                  <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                  <Link
                    to={`/organizations/${child.id}`}
                    className="hover:underline hover:text-primary"
                  >
                    {child.name}
                  </Link>
                  <div className="ml-2">
                    <Badge variant={child.status === "active" ? "outline" : "secondary"}>
                      {child.status}
                    </Badge>
                  </div>
                  
                  {showMetrics && child.metrics && (
                    <div className="ml-auto flex items-center space-x-2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center">
                        <Building2 className="h-3 w-3 mr-1" />
                        <span>{child.metrics.childOrganizationCount || 0}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No sub-organizations found</div>
          )}
        </div>
      </div>
    );
  };

  // Render metrics view
  const renderMetricsView = () => {
    if (!hierarchyMetrics || !hierarchyData.rootNode) {
      return (
        <div className="text-center py-6 text-muted-foreground">
          <p>No metrics data available.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="font-medium text-base mb-3">Hierarchy Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">{hierarchyMetrics.organizationCount}</div>
              <div className="text-sm text-muted-foreground">Total Organizations</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">{hierarchyMetrics.metrics.max_depth}</div>
              <div className="text-sm text-muted-foreground">Max Depth</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">
                {hierarchyData.flattenedHierarchy.filter(o => o.status === "active").length}
              </div>
              <div className="text-sm text-muted-foreground">Active Organizations</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">
                {hierarchyData.flattenedHierarchy.filter(o => o.status !== "active").length}
              </div>
              <div className="text-sm text-muted-foreground">Inactive Organizations</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <h3 className="font-medium text-base mb-3">Business Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">{hierarchyMetrics.metrics.total_contacts}</div>
              <div className="text-sm text-muted-foreground">Total Contacts</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">{hierarchyMetrics.metrics.total_opportunities}</div>
              <div className="text-sm text-muted-foreground">Total Opportunities</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">
                ${hierarchyMetrics.metrics.total_opportunity_value.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Total Pipeline Value</div>
            </div>
            <div className="bg-muted p-3 rounded text-center">
              <div className="text-2xl font-bold">{hierarchyMetrics.metrics.total_activities}</div>
              <div className="text-sm text-muted-foreground">Total Activities</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <h3 className="font-medium text-base mb-3">Organization Tiers</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tierOptions.map(tier => {
              const count = hierarchyData.flattenedHierarchy.filter(o => o.tier === tier).length;
              const percentage = Math.round(
                (count / hierarchyData.flattenedHierarchy.length) * 100
              );
              
              return (
                <div key={tier} className="bg-muted p-3 rounded text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm text-muted-foreground capitalize">{tier} ({percentage}%)</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
        <span>Loading organization hierarchy...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-500">
        <p>{error}</p>
      </div>
    );
  }

  if (!hierarchyData.rootNode) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <p>No organization hierarchy data found.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Controls */}
      {showControls && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center space-x-2">
            <TabsList>
              <TabsTrigger
                value="tree"
                onClick={() => setView("tree")}
                data-state={view === "tree" ? "active" : ""}
              >
                <Map className="h-4 w-4 mr-2" />
                Tree
              </TabsTrigger>
              <TabsTrigger 
                value="grid"
                onClick={() => setView("grid")}
                data-state={view === "grid" ? "active" : ""}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Grid
              </TabsTrigger>
              <TabsTrigger
                value="path"
                onClick={() => setView("path")}
                data-state={view === "path" ? "active" : ""}
              >
                <ChevronsRight className="h-4 w-4 mr-2" />
                Path
              </TabsTrigger>
              {showMetrics && (
                <TabsTrigger
                  value="metrics"
                  onClick={() => setView("metrics")}
                  data-state={view === "metrics" ? "active" : ""}
                >
                  <BarChart className="h-4 w-4 mr-2" />
                  Metrics
                </TabsTrigger>
              )}
            </TabsList>
            
            {view === "tree" && (
              <>
                <Button variant="outline" size="icon" onClick={expandAll}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={collapseAll}>
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {showSearch && (view === "tree" || view === "grid") && (
              <div className="flex items-center">
                <Input
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 w-full sm:w-[200px]"
                />
              </div>
            )}
            
            {(view === "tree" || view === "grid") && (
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map(status => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {(view === "tree" || view === "grid") && (
              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger className="h-9 w-[120px]">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  {tierOptions.map(tier => (
                    <SelectItem key={tier} value={tier}>
                      {tier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {view !== "metrics" && (
              <Select
                value={maxDepth.toString()}
                onValueChange={(value) => setMaxDepth(parseInt(value))}
              >
                <SelectTrigger className="h-9 w-[120px]">
                  <SelectValue placeholder="Depth" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Depth: 1</SelectItem>
                  <SelectItem value="2">Depth: 2</SelectItem>
                  <SelectItem value="3">Depth: 3</SelectItem>
                  <SelectItem value="5">Depth: 5</SelectItem>
                  <SelectItem value="10">Depth: 10</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Button variant="outline" size="icon" onClick={exportHierarchy}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Visualization */}
      <div className="mt-2">
        {view === "tree" && (
          <ScrollArea className="h-[600px] pr-4">
            {renderTreeNode(hierarchyData.rootNode)}
          </ScrollArea>
        )}
        
        {view === "grid" && (
          <ScrollArea className="h-[600px] pr-4">
            {renderGridView()}
          </ScrollArea>
        )}
        
        {view === "path" && renderPathView()}
        
        {view === "metrics" && renderMetricsView()}
      </div>
      
      {/* Filtered Results Summary */}
      {(view === "tree" || view === "grid") && 
       (searchQuery || filterStatus !== "all" || filterTier !== "all") && (
        <div className="mt-4 text-sm text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>
              Showing {filteredOrganizations.length} of {hierarchyData.flattenedHierarchy.length} organizations
            </span>
            
            {showMetrics && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Users className="h-3 w-3 mr-1" />
                  <span>{filteredMetrics.contacts} contacts</span>
                </div>
                <div className="flex items-center">
                  <BarChart className="h-3 w-3 mr-1" />
                  <span>{filteredMetrics.opportunities} opportunities</span>
                </div>
                <div className="flex items-center">
                  <span>${filteredMetrics.opportunityValue.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}